import React, { useMemo } from 'react';
import { useIntl, type MessageDescriptor } from 'react-intl';
import { TypedMemo } from '../../../../common/types';
import { KIT_MISSING_BLOCKS, type KitMissingBlock } from './kitSlots';
import { kitMessages } from './kitMessages';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './Kit.module.scss';
/* eslint-enable css-modules/no-unused-class */

const BLOCK_MESSAGES: Record<KitMissingBlock, MessageDescriptor> = {
  master: kitMessages.blockMaster,
  lyrics: kitMessages.blockLyrics,
  releaseDay: kitMessages.blockReleaseDay,
  plan: kitMessages.blockPlan,
  editor: kitMessages.blockEditor,
  cover: kitMessages.blockCover,
  choice: kitMessages.blockChoice,
};

/** The count of the parts that "Make the N missing ones" leaves out. */
export function blockedCount(blocks: ReadonlyMap<KitMissingBlock, number>): number {
  return [...blocks.values()].reduce((sum, count) => sum + count, 0);
}

interface KitBlockedLineProps {
  blocks: ReadonlyMap<KitMissingBlock, number>;
}

/**
 * "Not in this click: 2 need the lyrics, 1 is made in the music video editor" (QA 2026-10-28,
 * imp-8, lit-8, mob-13). With the button count it adds up to the "not made" and "failed" chips, so
 * no part looks lost. Nothing when every missing part can be made now.
 */
const KitBlockedLineComponent: React.FC<KitBlockedLineProps> = ({ blocks }) => {
  const intl = useIntl();
  const parts = useMemo(
    () =>
      KIT_MISSING_BLOCKS.filter((block) => (blocks.get(block) ?? 0) > 0).map((block) =>
        intl.formatMessage(BLOCK_MESSAGES[block], { count: blocks.get(block) ?? 0 })
      ),
    [blocks, intl]
  );
  if (parts.length === 0) {
    return null;
  }
  return (
    <span className={styles.cost} data-testid="kit-blocked">
      {intl.formatMessage(kitMessages.missingBlockedLead)} {intl.formatList(parts, { type: 'conjunction' })}
    </span>
  );
};

export const KitBlockedLine = TypedMemo(KitBlockedLineComponent);
